import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import ContentManager from 'src/componentes/content-manager';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';
import Confirm from 'src/componentes/message-box/confirm';
import Button from 'src/componentes/button';
import useAuth from 'src/hooks/useAuth';
import StageValidateDialog from './StageValidateDialog';
import { STATUS_ETAPA_STAGEGATE } from 'src/utils/constants';

const StageGateEditToolBar = ({
  form,
  model,
  handleSave,
  handleClose,
  refresh,
}) => {
  const intl = useIntl();
  const { terms: resources } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const stageValidateDialogRef = useRef(null);

  const handleSendApproval = async () => {
    try {
      await http.post(`StageGate/EnviarParaAprovacao`, {
        projeto: { id: model.getModel('id').value },
        stageGate: model.getModel('stageGate').value,
        etapaId: model.getModel('etapaAtualStageGate').value?.id,
      });
      enqueueSnackbar(
        intl.formatMessage(
          { id: 'stageGateEstagioSubmetidoAprovacaoSucesso' },
          { projeto: resources.projeto }
        ),
        {
          variant: 'success',
        }
      );
      refresh();
    } catch (error) {
      errorHandler(error);
    }
  };

  const handleConfirmSendApproval = () => {
    ContentManager.addContent(
      <Confirm
        message={intl.formatMessage(
          {
            id: 'enviarParaAprovacaoConfirmacao',
          },
          { projeto: resources.projeto }
        )}
        handleConfirm={handleSendApproval}
      />
    );
  };

  const handleProceed = async (stageApproveStatus, comment) => {
    if (stageApproveStatus.key === 'approve') {
      try {
        await http.post(`StageGate/Aprovar`, {
          validacaoId: model.getModel('etapaExibidaStageGate').value
            ?.validacaoId,
          projeto: { id: model.getModel('id').value },
          stageGate: model.getModel('stageGate').value,
          etapaId: model.getModel('etapaExibidaStageGate').value?.id,
          observacao: comment,
        });
        enqueueSnackbar(
          intl.formatMessage(
            { id: 'stageGateEstagioValidadoSucesso' },
            { projeto: resources.projeto }
          ),
          {
            variant: 'success',
          }
        );
        refresh();
      } catch (error) {
        errorHandler(error);
      }
    } else if (stageApproveStatus.key === 'review') {
      try {
        await http.post(`StageGate/Revisar`, {
          validacaoId: model.getModel('etapaExibidaStageGate').value
            ?.validacaoId,
          projeto: { id: model.getModel('id').value },
          stageGate: model.getModel('stageGate').value,
          etapaId: model.getModel('etapaExibidaStageGate').value?.id,
          observacao: comment,
        });
        enqueueSnackbar(
          intl.formatMessage(
            { id: 'stageGateEstagioValidadoSucesso' },
            { projeto: resources.projeto }
          ),
          {
            variant: 'success',
          }
        );
        refresh();
      } catch (error) {
        errorHandler(error);
      }
    } else if (stageApproveStatus.key === 'suspend') {
      try {
        await http.post(`StageGate/Suspender`, {
          validacaoId: model.getModel('etapaExibidaStageGate').value
            ?.validacaoId,
          projeto: { id: model.getModel('id').value },
          stageGate: model.getModel('stageGate').value,
          etapaId: model.getModel('etapaExibidaStageGate').value?.id,
          observacao: comment,
        });
        enqueueSnackbar(
          intl.formatMessage(
            { id: 'stageGateEstagioValidadoSucesso' },
            { projeto: resources.projeto }
          ),
          {
            variant: 'success',
          }
        );
        refresh();
      } catch (error) {
        errorHandler(error);
      }
    } else if (stageApproveStatus.key === 'reject') {
      try {
        await http.post(`StageGate/Reprovar`, {
          validacaoId: model.getModel('etapaExibidaStageGate').value
            ?.validacaoId,
          projeto: { id: model.getModel('id').value },
          stageGate: model.getModel('stageGate').value,
          etapaId: model.getModel('etapaExibidaStageGate').value?.id,
          observacao: comment,
        });
        enqueueSnackbar(
          intl.formatMessage(
            { id: 'stageGateEstagioValidadoSucesso' },
            { projeto: resources.projeto }
          ),
          {
            variant: 'success',
          }
        );
        refresh();
      } catch (error) {
        errorHandler(error);
      }
    }
  };

  const handleCloseStageValidateDialog = () => {
    if (stageValidateDialogRef.current) {
      ContentManager.removeContent(stageValidateDialogRef.current);
      stageValidateDialogRef.current = null;
    }
  };

  const handleStageValidate = () => {
    stageValidateDialogRef.current = ContentManager.addContent(
      <StageValidateDialog
        onClose={handleCloseStageValidateDialog}
        onProceed={handleProceed}
      />
    );
  };

  const etapaAtual = model.getModel('etapaExibidaStageGate').value;

  const allowApprove = etapaAtual?.permissions?.allowApprove;

  const allowSendToApproval =
    etapaAtual?.permissions?.allowSendToApproval &&
    model.getModel('id').value > 0 &&
    model.getModel('permissions').value.allowEdit &&
    (model.getModel('status').value.id == 1 ||
      model.getModel('status').value.id == 2);

  const hasPendindStatus =
    etapaAtual?.statusEtapa.id === STATUS_ETAPA_STAGEGATE.PENDENTE;

  return (
    <div className="pull-right mb-4">
      <Button color="secondary" onClick={handleClose}>
        {intl.formatMessage({ id: 'label.cancelar' })}
      </Button>
      {!allowApprove && !hasPendindStatus && (
        <Button
          className="ml-2"
          onClick={() => {
            handleSave();
          }}
        >
          {intl.formatMessage({ id: 'label.salvar' })}
        </Button>
      )}
      {allowSendToApproval && (
        <Button
          className="ml-2"
          color="primary"
          onClick={handleConfirmSendApproval}
          disabled={!form.isValid()}
        >
          {intl.formatMessage({ id: 'enviarParaAprovacao' })}
        </Button>
      )}
      {allowApprove && (
        <Button className="ml-2" color="primary" onClick={handleStageValidate}>
          {intl.formatMessage({ id: 'validar' })}
        </Button>
      )}
    </div>
  );
};

export default StageGateEditToolBar;
